"use client";

import React from "react";
import Link from "next/link";
import "./airlines-airports.css";

const questions = [
  "Why is a route growing but becoming less profitable?",
  "What is driving airport or airline operating cost?",
  "Which procurement categories are increasing fastest?",
  "Where are critical spares and inventory building?",
  "Which assets require management attention?",
  "Where are workforce skill or capacity gaps?",
  "Why are projects or maintenance activities slipping?",
  "What changed today that management needs to know?",
  "Can finance, operations, assets and people be viewed together?",
  "Where can AI improve a decision rather than simply produce another dashboard?",
];

const capabilities = [
  {
    number: "01",
    icon: "enterprise",
    title: "Enterprise Transformation",
    text: "S/4HANA | Finance | Procurement | Projects | Assets | BTP | Integration",
    image: "/assets/images/Enterprise-Transformation.jpg",
    href: "/enterprise-transformation",
  },
  {
    number: "02",
    icon: "people",
    title: "Human Transformation",
    text: "SuccessFactors | Crew & Workforce | Talent | Learning | Skills | Workforce Intelligence",
    image: "/assets/images/human-transforamtion.jpg",
    href: "/enterprise-transformation/successfactors",
  },
  {
    number: "03",
    icon: "data",
    title: "Data & AI Intelligence",
    text: "Enterprise Data | SAP BDC | Analytics | Data Science | ML | AI Agents | Decision Intelligence",
    image: "/assets/images/Data-&-AI-Intelligence.jpg",
    href: "/whatwedo/Ai-and-Intelligence",
  },
  {
    number: "04",
    icon: "settings",
    title: "Operate & Improve",
    text: "AMS | Applications | Integrations | Data Operations | Controls | Automation | Continuous Improvement",
    image: "/assets/images/Operate_improve.jpg",
    href: "/Operate",
  },
];

const valueChain = [
  {
    title: "ROUTE",
    text: "Network | Schedule | Revenue",
    image: "/assets/images/ROUTE (1).jpg",
  },
  {
    title: "FLIGHT",
    text: "Operations | Crew | Disruption",
    image: "/assets/images/FLIGHT.jpg",
  },
  {
    title: "AIRCRAFT",
    text: "Maintenance | Spares | Assets",
    image: "/assets/images/AIRCRAFT (1).jpg",
  },
  {
    title: "AIRPORT",
    text: "Ground Ops | Facilities | Services",
    image: "/assets/images/AIRPORT (1).jpg",
  },
  {
    title: "PASSENGER / CARGO",
    text: "Experience | Baggage | Cargo",
    image: "/assets/images/PASSENGER---CARGO (1).jpg",
  },
];

const valueLayers = [
  ["OPERATIONAL DATA", "Flights | Aircraft | Airports | Ground Operations | Cargo"],
  ["ENTERPRISE SYSTEMS", "ERP | People | Assets | Finance | Procurement | BTP"],
  ["DATA PLATFORM", "SAP BDC | Integration | External Data"],
];

const workedExample = [
  {
    title: "FLIGHT",
    value: "Addis – London",
    icon: "flight",
  },
  {
    title: "REVENUE",
    value: "Passenger\nCargo\nAncillary",
    icon: "revenue",
  },
  {
    title: "COST",
    value: "Fuel ↑\nGround Handling ↑\nCrew\nMaintenance\nAirport Charges",
    icon: "cost",
  },
  {
    title: "OTHER FACTORS",
    value: "Load Factor ↓\nMix Change\nWeather / Delay\nConnection Impact",
    icon: "factors",
  },
];

const workedActions = [
  ["database", "EXPLAIN", "with data"],
  ["settings", "SIMULATE", "options"],
  ["lightbulb", "RECOMMEND", "actions"],
  ["people", "HUMAN", "decision"],
];

const workedResults = [
  ["chart", "BETTER ROUTE ECONOMICS"],
  ["database", "LOWER COST"],
  ["shield", "HIGHER RELIABILITY"],
];

const aiUseCases = [
  {
    icon: "flight",
    title: "Day of Flight Intelligence",
    text: "Predict disruption, surface exceptions, recommend recovery options.",
  },
  {
    icon: "chart",
    title: "Flight & Route Economics",
    text: "Understand route cost, revenue and profitability by flight and route.",
  },
  {
    icon: "tools",
    title: "Asset & Spare Intelligence",
    text: "Predict spare demand, detect abnormal consumption, optimise inventory.",
  },
  {
    icon: "cart",
    title: "Procurement Intelligence",
    text: "Supplier risk, price variance, spend anomalies and contract insights.",
  },
  {
    icon: "people",
    title: "Workforce Intelligence",
    text: "Skills, training, deployment, capacity and workforce planning.",
  },
  {
    icon: "airport",
    title: "Airport Operations Intelligence",
    text: "Gate / stand capacity, service levels and operational bottlenecks.",
  },
  {
    icon: "finance",
    title: "Finance & Cash Intelligence",
    text: "Revenue, forecast, cash, exceptions and faster close.",
  },
];

const systems = [
  ["SAP", "S/4HANA", "enterprise"],
  ["SuccessFactors", "People", "people"],
  ["Ariba", "Procurement", "procurement"],
  ["EAM", "Assets", "assets"],
  ["Concur", "Expenses", "finance"],
  ["BTP", "Integration", "integration"],
  ["Other Systems", "External Data", "data"],
];

const semanticContext = [
  "Route",
  "Flight",
  "Aircraft",
  "Airport",
  "Asset",
  "People",
  "Finance",
  "Supplier",
];

function Icon({ type, size = 28 }) {
  const common = {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.8",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": true,
  };

  switch (type) {
    case "enterprise":
      return (
        <svg {...common}>
          <path d="M4 20V9l8-5 8 5v11" />
          <path d="M8 20v-5h8v5" />
          <path d="M9 9h.01M15 9h.01M9 12h.01M15 12h.01" />
        </svg>
      );

    case "people":
      return (
        <svg {...common}>
          <circle cx="9" cy="8" r="3" />
          <circle cx="17" cy="9" r="2.5" />
          <path d="M3.5 20c.5-4 2.5-6 5.5-6s5 2 5.5 6" />
          <path d="M14 14c3.2-.5 5.5 1.5 6 5" />
        </svg>
      );

    case "data":
      return (
        <svg {...common}>
          <path d="M5 19V9" />
          <path d="M12 19V5" />
          <path d="M19 19v-7" />
          <path d="M3 19h18" />
        </svg>
      );

    case "settings":
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1-1.8 1.8-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.6V20h-2.6v-.1a1.7 1.7 0 0 0-1-1.6 1.7 1.7 0 0 0-1.9.3l-.1.1-1.8-1.8.1-.1A1.7 1.7 0 0 0 8 15a1.7 1.7 0 0 0-1.6-1H6v-2.6h.4A1.7 1.7 0 0 0 8 10.4a1.7 1.7 0 0 0-.3-1.9l-.1-.1 1.8-1.8.1.1a1.7 1.7 0 0 0 1.9.3 1.7 1.7 0 0 0 1-1.6V5h2.6v.4a1.7 1.7 0 0 0 1 1.6 1.7 1.7 0 0 0 1.9-.3l.1-.1 1.8 1.8-.1.1a1.7 1.7 0 0 0-.3 1.9 1.7 1.7 0 0 0 1.6 1h.4V14h-.4a1.7 1.7 0 0 0-1.6 1Z" />
        </svg>
      );

    case "route":
      return (
        <svg {...common}>
          <circle cx="5" cy="18" r="2" />
          <circle cx="19" cy="6" r="2" />
          <path d="M7 17c4-1 4-7 10-10" />
        </svg>
      );

    case "flight":
      return (
        <svg {...common}>
          <path d="M3 12h18" />
          <path d="m12 12 2-8 2 8-2 8-2-8Z" />
          <path d="m8 12-5-4" />
          <path d="m8 12-5 4" />
        </svg>
      );

    case "engine":
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="8" />
          <circle cx="12" cy="12" r="3" />
          <path d="M12 4v5M20 12h-5M12 20v-5M4 12h5" />
        </svg>
      );

    case "airport":
      return (
        <svg {...common}>
          <path d="M3 20h18" />
          <path d="M5 20V8h14v12" />
          <path d="M8 8V5h8v3" />
          <path d="M8 12h8M8 16h8" />
        </svg>
      );

    case "cargo":
      return (
        <svg {...common}>
          <path d="M4 7h16v13H4z" />
          <path d="M8 7V4h8v3" />
          <path d="M4 11h16" />
          <path d="M9 11v9" />
        </svg>
      );

    case "revenue":
      return (
        <svg {...common}>
          <path d="M5 19V9M12 19V5M19 19v-8" />
          <path d="M3 19h18" />
        </svg>
      );

    case "cost":
      return (
        <svg {...common}>
          <path d="M4 5h16v14H4z" />
          <path d="M8 9h8M8 13h5M8 17h3" />
        </svg>
      );

    case "factors":
      return (
        <svg {...common}>
          <path d="M4 18h16" />
          <path d="m5 15 4-5 3 3 6-8" />
          <path d="M15 5h3v3" />
        </svg>
      );

    case "tools":
      return (
        <svg {...common}>
          <path d="m14 6 4 4" />
          <path d="m5 19 7-7" />
          <path d="m16 4 4 4" />
          <path d="M7 17 4 20" />
          <path d="M15 4a4 4 0 0 0-5 5l-5 5a2 2 0 1 0 3 3l5-5a4 4 0 0 0 5-5" />
        </svg>
      );

    case "cart":
      return (
        <svg {...common}>
          <path d="M4 5h2l2 10h10l2-7H7" />
          <circle cx="10" cy="19" r="1.5" />
          <circle cx="18" cy="19" r="1.5" />
        </svg>
      );

    case "finance":
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="8" />
          <path d="M12 7v10M15 9.5c-.7-.7-1.6-1-3-1-1.5 0-2.5.7-2.5 1.8 0 2.7 5.5 1.1 5.5 4 0 1.2-1.1 2-3 2-1.2 0-2.2-.3-3-1" />
        </svg>
      );

    case "procurement":
      return (
        <svg {...common}>
          <path d="M4 5h2l2 10h10l2-7H7" />
          <circle cx="10" cy="19" r="1.5" />
          <circle cx="18" cy="19" r="1.5" />
        </svg>
      );

    case "assets":
      return (
        <svg {...common}>
          <path d="m14 6 4 4" />
          <path d="m5 19 7-7" />
          <path d="m16 4 4 4" />
          <path d="M7 17 4 20" />
          <path d="M15 4a4 4 0 0 0-5 5l-5 5a2 2 0 1 0 3 3l5-5a4 4 0 0 0 5-5" />
        </svg>
      );

    case "integration":
      return (
        <svg {...common}>
          <circle cx="12" cy="5" r="2.3" />
          <circle cx="5" cy="18" r="2.3" />
          <circle cx="19" cy="18" r="2.3" />
          <path d="M12 7.3 7 15.7" />
          <path d="M12 7.3 17 15.7" />
          <path d="M7.3 18h9.4" />
        </svg>
      );

    case "database":
      return (
        <svg {...common}>
          <ellipse cx="12" cy="5" rx="8" ry="3" />
          <path d="M4 5v14c0 1.7 3.6 3 8 3s8-1.3 8-3V5" />
          <path d="M4 12c0 1.7 3.6 3 8 3s8-1.3 8-3" />
        </svg>
      );

    case "layers":
      return (
        <svg {...common}>
          <path d="M12 3 3 8l9 5 9-5-9-5z" />
          <path d="M3 13l9 5 9-5" />
          <path d="M3 17l9 5 9-5" />
        </svg>
      );

    case "chart":
      return (
        <svg {...common}>
          <path d="M4 19h16" />
          <path d="M7 19v-6M12 19V9M17 19v-10" />
          <path d="m5 10 4-4 3 3 6-6" />
        </svg>
      );

    case "lightbulb":
      return (
        <svg {...common}>
          <path d="M9 18h6" />
          <path d="M10 21h4" />
          <path d="M12 3a6 6 0 0 0-3.5 10.9c.6.5 1 1.3 1 2.1h5c0-.8.4-1.6 1-2.1A6 6 0 0 0 12 3Z" />
        </svg>
      );

    case "shield":
      return (
        <svg {...common}>
          <path d="M12 3 5 6v5c0 4.5 2.9 7.8 7 10 4.1-2.2 7-5.5 7-10V6l-7-3Z" />
          <path d="m9 12 2 2 4-4" />
        </svg>
      );

    default:
      return null;
  }
}

export default function AirlinesAirportsPage() {
  return (
    <main className="airlines-page">

      {/* =====================================================
          HERO
      ====================================================== */}
      <section className="airlines-hero">
        <div className="airlines-hero-image-wrap">
          <img
            src="/assets/images/airlines-hero.jpg"
            alt="Airlines and Airports"
          />
        </div>

        <div className="airlines-hero-overlay" />

        <div className="airlines-container airlines-hero-inner">

          <div className="airlines-hero-content">
          

            <h1>
              Connected Aviation.
              <br />
              Better Decisions.
              <br />
              Stronger Economics.
            </h1>

            <p className="airlines-hero-lead">
              Operations. People. Data. AI.
            </p>

            <p className="airlines-hero-description">
              Connect operations, enterprise data, assets and people
              around the decisions that matter.
            </p>

            <div className="airlines-hero-buttons">
              <Link href="/tellusyourproblem#tell-details" className="airlines-btn primary">
                Tell Us Your Airlines  Problem
                <span>→</span>
              </Link>

              <a href="#capabilities" className="airlines-btn secondary">
                See How It Works
                <span className="airlines-play-icon">▶</span>
              </a>
            </div>

            <div className="airlines-hero-stats">
              {[
                ["flight", "HIGHER", "ON-TIME PERFORMANCE"],
                ["data", "BETTER", "ROUTE ECONOMICS"],
                ["settings", "OPTIMISED", "ASSETS & SPARES"],
                ["people", "MORE PRODUCTIVE", "PEOPLE"],
                ["cost", "LOWER", "OPERATING COST"],
                ["enterprise", "SAFER", "OPERATIONS"],
                ["revenue", "A MORE PROFITABLE", "TOMORROW"],
              ].map(([icon, line1, line2]) => (
                <div className="airlines-hero-benefit" key={line1}>
                  <div className="airlines-hero-benefit-icon">
                    <Icon type={icon} size={22} />
                  </div>
                  <span>
                    {line1}
                    <br />
                    {line2}
                  </span>
                </div>
              ))}
            </div>
          </div>

        </div>
      </section>

      {/* =====================================================
          QUESTIONS
      ====================================================== */}
      <section className="airlines-section" id="questions">
        <div className="airlines-container">

          <div className="airlines-questions-card">

            <div className="airlines-questions-content">

              <div className="section-heading">
                <span className="section-kicker">
                  REAL BUSINESS QUESTIONS
                </span>

                <h2>Key Questions We Help Answer</h2>

                <p>
                  Business questions that connect operational reality
                  to financial and operational outcomes — across the
                  full aviation value chain.
                </p>
              </div>

              <div className="airlines-questions-list">
                {questions.map((question, index) => (
                  <div className="airlines-question-item" key={question}>
                    <span className="airlines-question-number">
                      {String(index + 1).padStart(2, "0")}
                    </span>

                    <p>{question}</p>

                    <span className="airlines-question-arrow">→</span>
                  </div>
                ))}
              </div>

              <a href="#value-chain" className="airlines-questions-more">
                Explore The Value Chain
                <span>→</span>
              </a>

            </div>

            <div className="airlines-questions-image">
              <img
                src="/assets/images/REAL-BUSINESS-QUESTIONS.jpg"
                alt="Business leaders reviewing key questions on a tablet"
              />
            </div>

          </div>
        </div>
      </section>

      {/* =====================================================
          CAPABILITIES
      ====================================================== */}
      <section className="airlines-section" id="capabilities">
        <div className="airlines-container">

          <div className="section-heading">
            <span className="section-kicker">
              END-TO-END ENTERPRISE CAPABILITY
            </span>

            <h2>Orane for Airlines & Airports</h2>

            <p>
              Enterprise capability with AI at the core.
            </p>
          </div>

          <div className="capabilities-grid">
            {capabilities.map((item) => (
              <Link
                href={item.href}
                className="capability-card"
                key={item.number}
              >
                <div
                  className="capability-media"
                  style={{ backgroundImage: `url("${item.image}")` }}
                />

                <div className="capability-icon">
                  <Icon type={item.icon} size={26} />
                </div>

                <h3>{item.title}</h3>

                <ul className="capability-list">
                  {item.text.split("|").map((t) => (
                    <li key={t.trim()}>{t.trim()}</li>
                  ))}
                </ul>

                <span className="capability-arrow" aria-hidden="true">
                  →
                </span>
              </Link>
            ))}
          </div>

          <div className="connected-banner">
            <span>ONE CONNECTED AVIATION ENTERPRISE</span>
            <p>
              People <b>•</b> Processes <b>•</b> Data <b>•</b> AI{" "}
              <b>•</b> A More Resilient Tomorrow
            </p>
          </div>

        </div>
      </section>

      {/* =====================================================
          VALUE CHAIN
      ====================================================== */}
      <section className="airlines-section" id="value-chain">
        <div className="airlines-container">

          <div className="section-heading">
            <span className="section-kicker">
              CONNECTED VALUE CHAIN
            </span>

            <h2>The Aviation Value Chain</h2>

            <p>
              Connecting operations to enterprise value.
            </p>
          </div>

          <div className="value-chain">
            {valueChain.map((item) => (
              <div
                className="value-chain-item"
                key={item.title}
                style={{ backgroundImage: `url("${encodeURI(item.image)}")` }}
                tabIndex={0}
              >
                <span className="value-chain-label">{item.title}</span>

                <div className="value-chain-reveal">
                  <p>{item.text}</p>
                  <span className="value-chain-arrow">→</span>
                </div>
              </div>
            ))}
          </div>

          <div className="value-layers">
            {valueLayers.map(([title, text]) => (
              <div className="value-layer" key={title}>
                <strong>{title}</strong>
                <span>{text}</span>
              </div>
            ))}
          </div>

          <div className="ai-strip">
            <span>AI & AGENTS</span>
            <p>
              Detect <b>→</b> Explain <b>→</b> Predict{" "}
              <b>→</b> Simulate <b>→</b> Recommend
            </p>
          </div>

        </div>
      </section>

      {/* =====================================================
          WORKED EXAMPLE (redesigned: workedx-*)
      ====================================================== */}
      <section className="airlines-section">
        <div className="airlines-container">

          <div className="section-heading">
            <span className="section-kicker">
              ILLUSTRATIVE ANALYSIS
            </span>

            <h2>A Worked Example</h2>

            <p>
              Why did yesterday's Addis–London operation contribute
              less than expected?
            </p>
          </div>

          <div className="workedx-wrap">

            <div className="workedx-subject">
              <div className="workedx-subject-icon">
                <Icon type={workedExample[0].icon} size={26} />
              </div>
              <span className="workedx-subject-label">
                {workedExample[0].title}
              </span>
              <strong className="workedx-subject-value">
                {workedExample[0].value}
              </strong>
            </div>

            <div className="workedx-connector" aria-hidden="true">
              →
            </div>

            <div className="workedx-factors">
              {workedExample.slice(1).map((item, index) => (
                <div className="workedx-factor" key={item.title}>
                  {index !== 0 && (
                    <span className="workedx-factor-arrow" aria-hidden="true">
                      →
                    </span>
                  )}

                  <div className="workedx-factor-icon">
                    <Icon type={item.icon} size={22} />
                  </div>

                  <div className="workedx-factor-text">
                    <span>{item.title}</span>
                    <p>
                      {item.value.split("\n").map((line) => (
                        <span key={line}>{line}</span>
                      ))}
                    </p>
                  </div>
                </div>
              ))}
            </div>

          </div>

          <div className="workedx-flow">
            {workedActions.map(([icon, title, text], index) => (
              <React.Fragment key={title}>
                <div className="workedx-step">
                  <div className="workedx-step-icon">
                    <Icon type={icon} size={24} />
                  </div>

                  <div className="workedx-step-text">
                    <strong>{title}</strong>
                    <span>{text}</span>
                  </div>
                </div>

                {index !== workedActions.length - 1 && (
                  <span className="workedx-step-arrow" aria-hidden="true">
                    →
                  </span>
                )}
              </React.Fragment>
            ))}
          </div>

          <div className="workedx-result">
            {workedResults.map(([icon, label], index) => (
              <span className="workedx-result-item" key={label}>
                {index !== 0 && <b aria-hidden="true">|</b>}
                <Icon type={icon} size={16} />
                {label}
              </span>
            ))}
          </div>

        </div>
      </section>

      {/* =====================================================
          AI USE CASES (redesigned: aiuc-*)
      ====================================================== */}
      <section className="airlines-section">
        <div className="airlines-container">

          <div className="section-heading">
            <span className="section-kicker">
              FROM INSIGHT TO ACTION
            </span>

            <h2>AI Use Cases for Aviation</h2>

            <p>
              Real business impact across the aviation enterprise.
            </p>
          </div>

          <div className="aiuc-grid">
            {aiUseCases.map((item) => (
              <article className="aiuc-card" key={item.title}>
                <div className="aiuc-icon">
                  <Icon type={item.icon} size={20} />
                </div>

                <span className="aiuc-divider" aria-hidden="true" />

                <div className="aiuc-text">
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </div>

                <span className="aiuc-arrow" aria-hidden="true">
                  →
                </span>
              </article>
            ))}
          </div>

        </div>
      </section>

      {/* =====================================================
          SYSTEMS & DATA
      ====================================================== */}
      <section className="airlines-section">
        <div className="airlines-container">

          <div className="section-heading">
            <span className="section-kicker">
              ENTERPRISE DATA FOUNDATION
            </span>

            <h2>Enterprise Systems & Data</h2>

            <p>
              Bring Data Together. Add Aviation Context. Deliver Intelligence.
            </p>
          </div>

          <div className="systems-grid">
            {systems.map(([name, label, icon], index) => (
              <div
                className={`system-card ${index === 0 ? "system-card-primary" : ""}`}
                key={name}
              >
                <div className="system-icon">
                  <Icon type={icon} size={20} />
                </div>

                <strong>{name}</strong>
                <span>{label}</span>
              </div>
            ))}
          </div>

          <div className="data-boxes">

            <div className="data-box">
              <Icon type="database" size={24} />
              <div className="data-box-text">
                <strong>Trusted Aviation Data</strong>
                <span>Operational | Enterprise | External</span>
              </div>
            </div>

            <div className="data-box">
              <Icon type="layers" size={24} />
              <div className="data-box-text">
                <strong>Aviation Semantics & Context</strong>
                <span>{semanticContext.join(" | ")}</span>
              </div>
            </div>

            <div className="data-box">
              <Icon type="data" size={24} />
              <div className="data-box-text">
                <strong>AI, Analytics & Agents</strong>
                <span>Prediction | Simulation | Recommendations</span>
              </div>
            </div>

          </div>

        </div>
      </section>

      {/* =====================================================
          FINAL CTA
      ====================================================== */}
      <section className="airlines-final-cta" id="airlines-cta">

        <div className="airlines-final-bg" />

        <div className="airlines-container airlines-final-cta-inner">

          <div className="airlines-final-cta-text">

            <h2>
              Every Flight Is an Operation.
              <br />
              Every Operation Has an Economic Story.
            </h2>

            <Link
              href="/tellusyourproblem#tell-details"
              className="airlines-btn primary"
            >
              Tell Us Your Airlines & Airports Problem
              <span>→</span>
            </Link>

          </div>

          <div className="airlines-final-cta-stats">

            <div className="airlines-final-cta-stat">
              <div className="airlines-final-cta-stat-icon">
                <Icon type="flight" size={20} />
              </div>
              <span>Better<br />On-Time Performance</span>
            </div>

            <div className="airlines-final-cta-stat">
              <div className="airlines-final-cta-stat-icon">
                <Icon type="revenue" size={20} />
              </div>
              <span>Stronger<br />Route Economics</span>
            </div>

            <div className="airlines-final-cta-stat">
              <div className="airlines-final-cta-stat-icon">
                <Icon type="cost" size={20} />
              </div>
              <span>Lower<br />Operating Cost</span>
            </div>

            <div className="airlines-final-cta-stat">
              <div className="airlines-final-cta-stat-icon">
                <Icon type="people" size={20} />
              </div>
              <span>More Resilient<br />Ecosystem</span>
            </div>

          </div>

        </div>

      </section>

    </main>
  );
}